import { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, Trash2, Plus, X, Menu, Database, Clock, ExternalLink, Edit2, Check } from 'lucide-react';
import { chatinstance } from '@/services/axiosConfig';
import { Button, Spin, Empty, Tooltip, Input, message as antdMessage, Select } from 'antd';
import "./bot.css";
import ChatInput from './ChatInput';
  import ReactMarkdown from 'react-markdown';  
import renderMessageContent from './renderMessageContent';
import SeaflashBotButton from './Seaflashbotbutton';
 
export default function ChatbotWithHistory() {
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [showSidebar, setShowSidebar] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const messagesEndRef = useRef(null);
    const currentConversationIdRef = useRef(null);
    const [selectedshipid, setSelectedShipId] = useState("")
    const CURRENT_USER_ID = localStorage.getItem("user_id");
  
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setShowSidebar(false);
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadConversations();
        }
    }, [isOpen]);

    useEffect(() => {
        if (activeConversation) {
            currentConversationIdRef.current = activeConversation.id;
            loadMessages(activeConversation.id);
            if (isMobile) {
                setShowSidebar(false);
            }
        }
    }, [activeConversation]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadConversations = async () => {
        try {
            const res = await chatinstance.get(`/chat/conversations/${CURRENT_USER_ID}`);
            const data = res.data || res;
            const convList = Array.isArray(data) ? data : [];
            setConversations(convList);

            // Set first conversation as active if none selected
            if (convList.length > 0 && !activeConversation) {
                setActiveConversation(convList[0]);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
            antdMessage.error('Failed to load conversations');
        }
    };

    const loadMessages = async (conversationId) => {
        if (!conversationId) return;

        setLoadingMessages(true);
        try {
            const res = await chatinstance.get(`/chat/conversations/${conversationId}/messages`);
            const msgs = res.data || res || [];
            setMessages(Array.isArray(msgs) ? msgs : []);
        } catch (error) {
            console.error('Error loading messages:', error);
            antdMessage.error('Failed to load messages');
            setMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    const createNewConversation = async () => {
        try {
            const res = await chatinstance.post(`/chat/conversations`, {
                userId: CURRENT_USER_ID
            });
            const newConv = res?.data || res;

            setConversations(prev => [newConv, ...prev]);
            setActiveConversation(newConv);
            currentConversationIdRef.current = newConv.id;
            setMessages([]);
            if (isMobile) setShowSidebar(false);
            antdMessage.success('New conversation created');
        } catch (error) {
            console.error('Error creating conversation:', error);
            antdMessage.error('Failed to create conversation');
        }
    };

    const sendMessage = async () => {
        if (!input.trim()) return;

        const userInput = input;
        setInput('');
        setLoading(true);

        // 🔍 Get current conversation ID from ref
        const conversationId = currentConversationIdRef.current;

        // 🔍 DEBUG LOGS
        console.log('\n' + '='.repeat(80));
        console.log('🔍 FRONTEND: Sending Message');
        console.log('='.repeat(80));
        console.log('📝 Message:', userInput);
        console.log('💬 Conversation ID from ref:', conversationId);
        console.log('   Type:', typeof conversationId);
        console.log('   Is null:', conversationId === null);
        console.log('   Is undefined:', conversationId === undefined);
        console.log('👤 User ID:', CURRENT_USER_ID);
        console.log('='.repeat(80) + '\n');

        // Optimistically add user message to UI
        const tempUserMsg = {
            id: Date.now(),
            role: 'user',
            content: userInput,
            createdAt: new Date().toISOString(),
            meta: {}
        };
        setMessages(prev => [...prev, tempUserMsg]);

        try {
            // 🔍 Build request payload with proper field names
            const payload = {
                conversationId: conversationId || null,  // Use camelCase to match backend alias
                userId: CURRENT_USER_ID,
                message: userInput
            };

            console.log('📦 Request payload:', JSON.stringify(payload, null, 2));

            const res = await chatinstance.post(`/chat/message`, payload);

            console.log('✅ Response received:', res.data);

            const data = res.data || res;

            // Extract the actual data (handle both {data: ...} and direct response)
            const responseData = data.data || data;

            console.log('📦 Response data structure:', {
                hasConversation: !!responseData.conversation,
                hasUserMessage: !!responseData.userMessage,
                hasAssistantMessage: !!responseData.assistantMessage,
                conversationId: responseData.conversation?.id
            });

            // Update conversation if new
            if (!conversationId && responseData.conversation) {
                console.log('🆕 New conversation created:', responseData.conversation.id);
                const newConv = {
                    id: responseData.conversation.id,
                    title: responseData.conversation.title,
                    userId: CURRENT_USER_ID,
                    createdAt: responseData.conversation.created_at,
                    lastMessageAt: responseData.conversation.last_message_at
                };

                setActiveConversation(newConv);
                currentConversationIdRef.current = newConv.id;
                setConversations(prev => [newConv, ...prev]);

                console.log('✅ Updated ref with new conversation ID:', newConv.id);
            } else if (conversationId) {
                console.log('✅ Used existing conversation:', conversationId);
            }

            // Replace temp message and add assistant response
            if (responseData.userMessage && responseData.assistantMessage) {
                setMessages(prev => {
                    const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
                    return [...withoutTemp, responseData.userMessage, responseData.assistantMessage];
                });
                console.log('✅ Messages updated in UI');
            }

            // Reload conversations to update timestamps
            loadConversations();

        } catch (error) {
            console.error('❌ Error sending message:', error);
            console.error('   Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });

            antdMessage.error(
                error.response?.data?.detail ||
                'Failed to send message. Please try again.'
            );

            // Remove temp message
            setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));

            const errorMsg = {
                id: Date.now(),
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                meta: { error: error.message },
                createdAt: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setLoading(false);
        }
    };


    const deleteConversation = async (convId, e) => {
        e?.stopPropagation();

        try {
            await chatinstance.delete(`/chat/conversations/${convId}`);
            const updated = conversations.filter(c => c.id !== convId);
            setConversations(updated);

            if (activeConversation?.id === convId) {
                const newActive = updated[0] || null;
                setActiveConversation(newActive);
                currentConversationIdRef.current = newActive?.id || null;
                setMessages([]);
            }

            antdMessage.success('Conversation deleted');
        } catch (error) {
            console.error('Error deleting conversation:', error);
            antdMessage.error('Failed to delete conversation');
        }
    };

    const startEditTitle = (conv, e) => {
        e?.stopPropagation();
        setEditingId(conv.id);
        setEditTitle(conv.title);
    };

    const saveTitle = async (convId, e) => {
        e?.stopPropagation();

        if (!editTitle.trim()) {
            antdMessage.warning('Title cannot be empty');
            return;
        }

        try {
            await chatinstance.patch(`/chat/conversations/${convId}`, {
                title: editTitle.trim()
            });

            // Update local state
            setConversations(prev =>
                prev.map(c => c.id === convId ? { ...c, title: editTitle.trim() } : c)
            );

            if (activeConversation?.id === convId) {
                setActiveConversation(prev => ({ ...prev, title: editTitle.trim() }));
            }

            setEditingId(null);
            antdMessage.success('Title updated');
        } catch (error) {
            console.error('Error updating title:', error);
            antdMessage.error('Failed to update title');
        }
    };

    const cancelEdit = (e) => {
        e?.stopPropagation();
        setEditingId(null);
        setEditTitle('');
    };

    const formatSQL = (sql) => {
        if (!sql) return null;
        return sql.replace(/\s+/g, ' ').trim();
    };
 
    if (!isOpen) {
        return (
            <SeaflashBotButton/>
        );
    }

    return (
        <>
            <div
                className="shadow-lg"
                style={{
                    position: 'fixed',
                    bottom: isMobile ? 0 : '20px',
                    right: isMobile ? 0 : '20px',
                    width: isMobile ? '100%' : '1000px',
                    height: isMobile ? '100vh' : '90%',
                    zIndex: 1000,
                    backgroundColor: '#f0f2f5',
                    borderRadius: isMobile ? 0 : '12px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'row',
                    border: '1px solid #d9d9d9'
                }}
            >
                {/* Sidebar */}
                <div
                    className={`${showSidebar ? 'd-flex' : 'd-none'} flex-column`}
                    style={{
                        width: '300px',
                        minWidth: '300px',
                        maxWidth: '300px',
                        height: '100%',
                        position: isMobile ? 'absolute' : 'relative',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: isMobile ? 10 : 1,
                        backgroundColor: '#fff',
                        borderRight: '1px solid #e8e8e8',
                        boxShadow: isMobile ? '2px 0 8px rgba(0,0,0,0.15)' : 'none'
                    }}
                >
                    {/* Sidebar Header */}
                    <div
                        className="row align-items-center px-3 py-2 shadow-sm mb-3 w-100"
                        style={{
                            borderRadius: '12px',
                            backgroundColor: '#F5FDF9',
                            border: '1px solid #CAF3D8',
                        }}
                    >
                        {/* Left Section - New Chat */}
                        <div onClick={createNewConversation}
                            className="col-md-6 d-flex align-items-center gap-1 rounded-3 cursor-pointer" style={{ backgroundColor: "#C9F2D7" }}>
                            <div className="d-flex gap-2">


                                {isMobile ? (
                                    <Button
                                        shape="circle"
                                        icon={<X size={16} />}
                                        onClick={() => setShowSidebar(false)}
                                        size="small"
                                    />
                                ) : ""}
                            </div>
                            <Plus size={18} className='app-color  me-1' />
                            <span style={{ fontWeight: 600, fontSize: '16px', color: '#099276' }}>
                                New Chat
                            </span>
                        </div>

                        {/* Right Section - App Title */}
                        <div className="col-md-6 text-md-end mt-2 mt-md-0">
                            <span className='fs-16 app-color fw-medium' >
                                Seaflash Bot
                            </span>
                        </div>
                    </div>


                    {/* Conversations List */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '8px',
                        backgroundColor: '#fafafa'
                    }}>
                        {conversations?.length ? conversations.map(conv => (
                            <div
                                key={conv.id}
                                onClick={() => {
                                    if (editingId !== conv.id) {
                                        setActiveConversation(conv);
                                        currentConversationIdRef.current = conv.id;
                                        if (isMobile) setShowSidebar(false);
                                    }
                                }}
                                style={{
                                    padding: '12px',
                                    marginBottom: '8px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    backgroundColor: activeConversation?.id === conv.id ? '#099276' : '#fff',
                                    color: activeConversation?.id === conv.id ? '#fff' : '#000',
                                    border: '1px solid',
                                    borderColor: activeConversation?.id === conv.id ? '#099276' : '#e8e8e8',
                                    transition: 'all 0.2s ease',
                                    boxShadow: activeConversation?.id === conv.id ? '0 2px 8px rgba(24,144,255,0.2)' : 'none'
                                }}
                                onMouseEnter={(e) => {
                                    if (activeConversation?.id !== conv.id) {
                                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeConversation?.id !== conv.id) {
                                        e.currentTarget.style.backgroundColor = '#fff';
                                    }
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
                                        {editingId === conv.id ? (
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <Input
                                                    value={editTitle}
                                                    onChange={(e) => setEditTitle(e.target.value)}
                                                    onPressEnter={(e) => saveTitle(conv.id, e)}
                                                    size="small"
                                                    style={{ marginBottom: '4px' }}
                                                    autoFocus
                                                />
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        icon={<Check size={12} />}
                                                        onClick={(e) => saveTitle(conv.id, e)}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={cancelEdit}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    marginBottom: '4px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {conv.title}
                                                </div>
                                                <small style={{
                                                    opacity: activeConversation?.id === conv.id ? 0.8 : 0.6,
                                                    fontSize: '12px'
                                                }}>
                                                    {new Date(conv.lastMessageAt).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </small>
                                            </>
                                        )}
                                    </div>
                                    {editingId !== conv.id && (
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <Tooltip title="Edit Title">
                                                <Button
                                                    size="small"
                                                    type="text"
                                                    icon={<Edit2 size={14} />}
                                                    onClick={(e) => startEditTitle(conv, e)}
                                                    style={{
                                                        color: activeConversation?.id === conv.id ? '#fff' : '#666',
                                                        padding: '4px'
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <Button
                                                    size="small"
                                                    type="text"
                                                    danger={activeConversation?.id !== conv.id}
                                                    icon={<Trash2 size={14} />}
                                                    onClick={(e) => deleteConversation(conv.id, e)}
                                                    style={{
                                                        color: activeConversation?.id === conv.id ? '#fff' : undefined,
                                                        padding: '4px'
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No conversations yet"
                                style={{ marginTop: '40px' }}
                            />
                        )}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    height: '100%',
                    backgroundColor: '#fff'
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#fafafa',
                        minHeight: '60px'
                    }}>
                        <div className="d-flex align-items-center justify-content-between" style={{ gap: '12px', flex: 1, minWidth: 0 }}>
                            {(isMobile || !showSidebar) && (
                                <Button
                                    icon={<Menu size={18} />}
                                    onClick={() => setShowSidebar(!showSidebar)}
                                />
                            )}
                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                <h6 style={{
                                    margin: 0,
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {activeConversation ? activeConversation.title : 'Seaflash AI Assistant'}
                                </h6>
                               
                            </div>
                           
                        </div>
                        <Button className='ms-2'
                            icon={<X size={20} />}
                            onClick={() => setIsOpen(false)}
                            type="text"
                        />
                    </div>

                    {/* Messages */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',
                        backgroundColor: '#f5f5f5'
                    }}>
                        {loadingMessages ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Spin size="large" tip="Loading messages..." />
                            </div>
                        ) : messages?.length ? messages.map((msg, idx) => (
                            <div
                                key={msg.id || idx}
                                style={{
                                    marginBottom: '16px',
                                    display: 'flex',
                                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                                }}
                            >
                                <div style={{
                                    maxWidth: '85%',
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    backgroundColor: msg.role === 'user' ? '#099276' : '#fff',
                                    color: msg.role === 'user' ? '#fff' : '#000',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    wordWrap: 'break-word',
                                    border: msg.role === 'assistant' ? '1px solid #f0f0f0' : 'none'
                                }}>
                                    <div style={{
                                        fontWeight: 600,
                                        marginBottom: '8px',
                                        fontSize: '13px',
                                        opacity: 0.9
                                    }}>
                                        {msg.role === 'assistant' ? <span className='text-success'>Seaflash Assistant</span> : ' You'}
                                    </div>

                                    <div style={{
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {renderMessageContent(msg.content, msg.meta)}
                                     </div>

                                    {/* Metadata sections remain the same */}
                                    {msg.meta?.chart_url && (
                                        <div style={{ marginTop: '12px' }}>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<ExternalLink size={14} />}
                                                href={msg.meta.chart_url}
                                                target="_blank"
                                            >
                                                Open Chart
                                            </Button>
                                        </div>
                                    )}

                                    {msg.meta?.sql_query && (
                                        <div style={{
                                            marginTop: '12px',
                                            padding: '8px',
                                            backgroundColor: msg.role === 'user' ? 'rgba(255,255,255,0.1)' : '#fafafa',
                                            borderRadius: '6px',
                                            fontSize: '12px'
                                        }}>
                                            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                <Database size={12} />
                                                <strong>SQL Query:</strong>
                                            </div>
                                            <code style={{
                                                display: 'block',
                                                wordBreak: 'break-all',
                                                fontFamily: 'monospace'
                                            }}>
                                                {formatSQL(msg.meta.sql_query)}
                                            </code> */}
                                        </div>
                                    )}

                                    {/* {msg.meta?.error && (
                                        <div style={{
                                            marginTop: '12px',
                                            padding: '8px',
                                            backgroundColor: '#fff2f0',
                                            border: '1px solid #ffccc7',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            color: '#cf1322'
                                        }}>
                                            <strong>Error:</strong> {msg.meta.error}
                                        </div>
                                    )} */}
                                </div>
                            </div>
                        )) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#8c8c8c'
                            }}>
                                <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                                <h6>Start a conversation</h6>
                                <p style={{ fontSize: '14px' }}>Try asking:</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                    <Button
                                        size="small"
                                        onClick={() => setInput("How many users are there?")}
                                    >
                                        How many users?
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setInput("Show me ME Performance charts")}
                                    >
                                        Show charts
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setInput("Last report for Kezar from Clock")}
                                    >
                                        Last Clock report
                                    </Button>
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                marginBottom: '16px'
                            }}>
                                <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <Spin size="small" />
                                    <span style={{ marginLeft: '12px', color: '#8c8c8c' }}>
                                        Seaflash is thinking...
                                    </span>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    <ChatInput
                        input={input}
                        setInput={setInput}
                        sendMessage={sendMessage}
                        loading={loading}
                    />
                </div>
            </div>

            {/* Mobile Overlay */}
            {isMobile && showSidebar && (
                <div
                    onClick={() => setShowSidebar(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        zIndex: 9
                    }}
                />
            )}
        </>
    );
}